import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is a SPAC?',
    a: 'A Special Purpose Acquisition Company \u2014 a shell corporation that IPOs with no business, parks the cash (usually ~$10 per share) in a trust account, and has a deadline (typically 18\u201324 months) to merge with a private company. The merger takes the private company public without a traditional IPO. If no deal closes by the deadline, the SPAC liquidates and returns the trust cash to shareholders.',
  },
  {
    q: 'Do SPACs pay dividends?',
    a: 'No. A SPAC is a pile of cash in a trust earning treasury interest \u2014 there is no business generating profits to distribute. That interest accrues inside the trust and is paid out only through redemption or liquidation, never as a regular dividend. DiviDrip classifies every SPAC as non-dividend for this reason.',
  },
  {
    q: 'What happens to my shares when a SPAC completes its merger?',
    a: 'Your SPAC shares automatically convert into shares of the combined company, and the ticker changes \u2014 CCIV holders became LCID (Lucid) holders, DWAC holders became DJT (Trump Media) holders. Share count is usually 1-for-1, but the old ticker dies. DiviDrip\u2019s weekly SEC EDGAR sweep detects the ticker change and links the old shell to the new symbol.',
  },
  {
    q: 'What is the redemption right?',
    a: 'Before the merger vote, every public SPAC shareholder can redeem shares for their pro-rata slice of the trust (roughly $10 plus accrued interest) regardless of how they vote. It is a money-back guarantee on the deal \u2014 and when most holders take it, the combined company closes with far less cash than advertised. High redemption rates (90%+ was common in 2022) are a major red flag.',
  },
  {
    q: 'Why do so many de-SPAC stocks fall after the merger?',
    a: 'Dilution. The sponsor typically receives 20% of the shell\u2019s shares nearly free (the \u201cpromote\u201d), IPO investors hold warrants that mint new shares when exercised, and PIPE investors often buy in below the trust price. Stack those on a business that was frequently pre-revenue, and the per-share math starts underwater. Academic studies of the 2020\u201321 wave found median post-merger returns deeply negative versus the market.',
  },
  {
    q: 'How does DiviDrip track SPAC lifecycle stages?',
    a: 'A weekly Friday job reads each SPAC\u2019s SEC EDGAR submissions feed. Form types and 8-K item codes mark each stage: a Form 425 or S-4 filing means a deal is announced, a DEFM14A merger proxy means the shareholder vote is pending, and the Super 8-K (Item 2.01) or a deregistration filing confirms the de-SPAC completed. A ticker change alone is NOT treated as completion \u2014 SPACs sometimes rename their symbol before the merger closes (Graf Global switched from GRAF to TONT months before its BIG3 deal was set to close), so DiviDrip shows those as \u201cTicker changed \u2014 merger pending\u201d until the closing filings land. The stage, the new ticker, and links to the source filings appear in the stock modal\u2019s Insights tab.',
  },
];

export default function LearnSpacLifecycle() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="The SPAC Lifecycle — From Blank-Check IPO to De-SPAC, Redemption Rights & Sponsor Dilution | DiviDrip"
      description="How SPACs work stage by stage: the trust account, deal announcement (8-K Item 1.01), the S-4 and DEFM14A proxy, redemption rights, the Super 8-K ticker change, and why sponsor promote and warrants dilute post-merger returns. Real cases: Lucid, Virgin Galactic, Trump Media, and the 2022 liquidation wave."
      canonical="https://twylightcrow.com/learn/spac-lifecycle"
      guidePath="/learn/spac-lifecycle"
      sectionLabel="Non-Div Basics"
      sectionAnchor="non-div-basics"
      kicker="Learn · Non-Dividend Stocks"
      h1="The SPAC Lifecycle &mdash; A Pile of Cash Hunting for a Company"
      datePublished="2026-09-04"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/units', label: 'Units, Warrants & Rights — what the U, W and R tickers of a SPAC actually are' },
        { to: '/learn/lifecycle-of-a-stock', label: 'The Lifecycle of a Stock — the four stages every company passes through' },
        { to: '/learn/pre-revenue-tech-biotech', label: 'Pre-Revenue Tech & Biotech — valuing companies with no earnings' },
        { to: '/learn/exit-strategy-non-dividend', label: 'Exit Strategy for Non-Dividend Stocks — when to sell' },
        { to: '/learn/short-squeeze-dynamics', label: 'Short Squeeze Dynamics — market microstructure events' },
      ]}
    >
      <h2 id="what-is-a-spac">What a SPAC actually is</h2>
      <p>
        A <strong>Special Purpose Acquisition Company</strong> (SPAC, or blank-check
        company) is a shell with no products, no revenue, and no employees beyond its
        sponsor team. It IPOs anyway &mdash; usually at <strong>$10.00 per unit</strong>{' '}
        &mdash; and locks nearly all of the cash in a <strong>trust account</strong>{' '}
        invested in treasuries. The sponsor then has a deadline, typically 18&ndash;24
        months, to find a private company and merge with it. That merger, called the{' '}
        <strong>de-SPAC</strong>, takes the private business public without a
        traditional IPO roadshow.
      </p>
      <p>
        Each unit usually bundles one common share plus a fraction of a{' '}
        <strong>warrant</strong> (a long-dated right to buy more stock at $11.50). The
        unit ticker often ends in <strong>U</strong>, the warrant in{' '}
        <strong>WS</strong> or <strong>WT</strong> &mdash; which is why the DiviDrip
        screener&rsquo;s SPAC filter and the{' '}
        <Link to="/warrants">Warrant Screener</Link> so often surface the same issuers.
      </p>

      <h2 id="stages">The five stages &mdash; and the SEC filing that marks each one</h2>
      <p>
        A SPAC&rsquo;s whole life is legible from its SEC filing feed. No press releases
        needed &mdash; the form types and 8-K item codes tell the story:
      </p>
      <ul>
        <li>
          <strong>1. Searching.</strong> The shell trades near trust value ($10-ish)
          while the sponsor hunts. Filings are boring: 10-Qs, 13Gs from arbitrage funds.
        </li>
        <li>
          <strong>2. Deal announced.</strong> An <strong>8-K with Item 1.01</strong>{' '}
          (&ldquo;Entry into a Material Definitive Agreement&rdquo;) lands, followed by{' '}
          <strong>Form 425</strong> communications and an <strong>S-4</strong>{' '}
          registration statement describing the merger. The stock can rip or yawn
          depending on how the market likes the target.
        </li>
        <li>
          <strong>3. Vote pending.</strong> The <strong>DEFM14A</strong> merger proxy
          goes out, setting the shareholder vote date and the{' '}
          <strong>redemption deadline</strong> &mdash; the last day to hand shares back
          for trust value.
        </li>
        <li>
          <strong>4. Completed (de-SPAC).</strong> The closing is announced in a{' '}
          <strong>&ldquo;Super 8-K&rdquo;</strong> (Item 2.01, completion of
          acquisition), the company files a name change, and{' '}
          <strong>the ticker changes</strong>. EDGAR&rsquo;s company record flips to the
          new symbol overnight.
        </li>
        <li>
          <strong>5. Liquidated.</strong> No deal by the deadline means a{' '}
          <strong>Form 25</strong> delisting notice and a <strong>Form 15</strong>{' '}
          deregistration. The trust is returned to shareholders, and the ticker dies.
        </li>
      </ul>
      <p>
        One wrinkle to know: a <strong>ticker change is not the same as a completed
        merger</strong>. SPACs sometimes rebrand their symbol while the deal is still
        pending &mdash; Graf Global switched from GRAF to TONT in July 2026, months
        before its BIG3 business combination was scheduled to close. The old symbol
        looks &ldquo;delisted&rdquo; in market data even though it is the same shell.
        DiviDrip labels those <strong>Ticker changed &mdash; merger pending</strong>{' '}
        and only calls a de-SPAC complete when the Super 8-K or deregistration filings
        actually land.
      </p>
      <p>
        DiviDrip&rsquo;s weekly Friday sweep reads exactly these signals from EDGAR for
        every tracked SPAC and shows the current stage &mdash; with links to the source
        filings &mdash; in the stock modal&rsquo;s <strong>Insights</strong> tab.
      </p>

      <h2 id="trust-redemption">The trust account and the redemption right</h2>
      <p>
        The trust is what makes a pre-deal SPAC one of the strangest instruments in the
        market: it has a <strong>hard floor</strong>. Before the merger vote, every
        public shareholder can <strong>redeem</strong> for a pro-rata slice of the trust
        &mdash; roughly $10 plus accrued interest &mdash; no matter how they vote. Buy
        below trust value and redeem, and you have built a tiny treasury-bill trade.
        That is why arbitrage funds dominate pre-deal SPAC ownership.
      </p>
      <p>
        The dark side: redemptions drain the deal. When 90%+ of holders redeem (routine
        in 2022), the combined company closes with a fraction of the advertised cash,
        and the sponsor often back-fills with dilutive PIPE financing on worse terms.
        High redemption rates are one of the loudest red flags a de-SPAC can wave.
      </p>

      <h2 id="dilution">Sponsor promote and warrant dilution &mdash; the hidden tax</h2>
      <p>
        The sponsor typically buys <strong>20% of the shell&rsquo;s shares for a
        nominal sum</strong> (the &ldquo;promote&rdquo;) as payment for finding the
        deal. Add the warrants minted to IPO investors and the PIPE shares sold below
        trust value, and a de-SPAC can carry 30&ndash;40% more claims on the business
        than the headline share count suggests. That structural dilution &mdash; layered
        on targets that were frequently pre-revenue &mdash; is the main reason academic
        studies of the 2020&ndash;21 wave found median post-merger returns deeply
        negative versus the market.
      </p>

      <h2 id="real-examples">Real outcomes &mdash; the good, the famous, and the wound-down</h2>
      <ul>
        <li>
          <strong>IPOA &rarr; SPCE (Virgin Galactic, 2019).</strong> Chamath
          Palihapitiya&rsquo;s first SPAC took Richard Branson&rsquo;s space-tourism
          venture public and kicked off the modern SPAC boom. SPCE spiked above $55 in
          2021 &mdash; then rode the classic de-SPAC decay for years as losses mounted.
        </li>
        <li>
          <strong>CCIV &rarr; LCID (Lucid Motors, 2021).</strong> Churchill Capital IV
          ran from $10 to nearly $60 on deal rumors alone &mdash; before the merger
          terms were even public. Holders who bought the rumor above $50 have never been
          close to whole. A masterclass in why pre-announcement SPAC prices can detach
          completely from trust value.
        </li>
        <li>
          <strong>DWAC &rarr; DJT (Trump Media, 2024).</strong> The most-watched de-SPAC
          ever: Digital World Acquisition Corp. announced its target in October 2021,
          then spent two and a half years in regulatory purgatory before finally closing
          in March 2024 and re-tickering as DJT. Proof that stage 2 to stage 4 can take
          years, not months.
        </li>
        <li>
          <strong>PSTH (Pershing Square Tontine, liquidated 2022).</strong> Bill
          Ackman&rsquo;s $4 billion SPAC &mdash; the largest ever &mdash; never closed a
          deal after its Universal Music structure was blocked, and returned the entire
          trust to shareholders in 2022. Liquidation is not a disaster: holders got
          their ~$20 trust value back. The wave that followed saw hundreds of 2021-vintage
          shells liquidate through 2022&ndash;23.
        </li>
      </ul>
      <p>
        And a live one from the DiviDrip database: <strong>Graf Global Corp.
        (GRAF)</strong> renamed its ticker to <strong>TONT</strong> in July 2026 ahead
        of its pending merger with <strong>BIG3 HoldCo LLC</strong> &mdash; Ice
        Cube&rsquo;s professional 3-on-3 basketball league &mdash; which is exactly the
        kind of symbol swap the weekly EDGAR sweep is built to catch and label
        correctly while the deal is still open.
      </p>

      <h2 id="dividrip">How DiviDrip handles SPACs</h2>
      <ul>
        <li>
          <strong>Classification.</strong> Blank-check companies are detected from SIC
          code 6770 and filing metadata, flagged non-dividend, and excluded from
          dividend screeners by default (the &ldquo;Allow SPAC &amp; Warrant&rdquo;
          checkbox re-admits them).
        </li>
        <li>
          <strong>Lifecycle stage.</strong> The Insights tab shows the current stage
          &mdash; Searching, Deal Announced, Vote Pending, Completed, or Liquidated
          &mdash; with citations to the SEC filings behind the call.
        </li>
        <li>
          <strong>Ticker changes.</strong> When a de-SPAC completes, the old ticker is
          marked inactive, the new symbol is linked, and stale shell data is queued for
          cleanup so it never pollutes the new company&rsquo;s history.
        </li>
        <li>
          <strong>Warrants.</strong> SPAC warrants get their own{' '}
          <Link to="/warrants">Warrant Screener</Link> with exercise prices, expiration
          dates, and 8-K redemption alerts.
        </li>
      </ul>
      <p>
        Bottom line: a pre-deal SPAC is a cash box with a money-back guarantee, and a
        post-deal SPAC is a young, heavily diluted company that skipped the IPO
        gauntlet. Know which one you are holding &mdash; and let the filings, not the
        press releases, tell you when it changes.
      </p>
      <h2 id="faq">FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>
    </LearnPage>
  );
}
